import React, { useState, useEffect } from 'react';
import {
  Card,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Typography,
} from '@mui/material';
import { useLanguage } from './LanguageContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadRatesData } from '../utils/ratesData';

function ExchangeCalculator() {
  const { translation } = useLanguage();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [amount1, setAmount1] = useState('');
  const [amount2, setAmount2] = useState('');
  const [currency1, setCurrency1] = useState('INR');
  const [currency2, setCurrency2] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [currencies, setCurrencies] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [urlUpdated, setUrlUpdated] = useState(false);

  // Load data on component mount
  useEffect(() => {
    fetchStaticData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check URL parameters and set currencies if present
  useEffect(() => {
    // First make sure we have currencies loaded
    if (Object.keys(currencies).length === 0) {
      return; // Wait until currencies are loaded
    }

    // Only try to set from URL params if they exist
    if (params.from && params.to) {
      const base = params.from.toUpperCase();
      const target = params.to.toUpperCase();
      
      // Only set if they are valid currencies
      if (currencies[base] && currencies[target]) {
        setCurrency1(base);
        setCurrency2(target);
        setUrlUpdated(true);
      } else {
        // Invalid currencies in URL, navigate back to home
        console.warn("Invalid currency codes in URL:", base, target);
        navigate('/', { replace: true });
      }
    }
  }, [params.from, params.to, currencies, navigate]);

  // Update URL when currencies change (but not during initial load from URL)
  useEffect(() => {
    // Don't update URL if currencies aren't fully loaded yet
    if (Object.keys(currencies).length === 0) {
      return;
    }
    
    // Only update URL if both currencies are valid and user has made a selection
    if (currency1 && currency2 && urlUpdated && 
        currencies[currency1] && currencies[currency2] && 
        location.pathname !== `/${currency1.toLowerCase()}-to-${currency2.toLowerCase()}`) {
      navigate(`/${currency1.toLowerCase()}-to-${currency2.toLowerCase()}`, { replace: true });
    }
  }, [currency1, currency2, urlUpdated, navigate, location.pathname, currencies]);

  // Update exchange rate when currencies change
  useEffect(() => {
    if (currency1 && currency2 && Object.keys(currencies).length > 0) {
      calculateExchangeRate();
    }
  }, [currency1, currency2, currencies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update amount2 when amount1 or exchange rate changes
  useEffect(() => {
    if (amount1 && exchangeRate) {
      // Calculate the value without formatting - only do the math
      const calculatedValue = parseFloat(amount1) * exchangeRate;
      // Store the raw value without formatting
      // Only update if the user is not currently editing amount2 directly
      // We can detect this by checking if the field has focus
      const amount2Field = document.querySelector('input[name="amount2"]');
      if (!amount2Field || document.activeElement !== amount2Field) {
        setAmount2(calculatedValue.toString());
      }
    } else {
      setAmount2('');
    }
  }, [amount1, exchangeRate]);

  // Fetch all static data at once
  const fetchStaticData = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      const ratesData = await loadRatesData();

      if (ratesData.formatted) {
        setLastUpdated(ratesData.formatted);
      }

      if (ratesData.currencies) {
        setCurrencies(ratesData.currencies);
      }

      if (ratesData.rates) {
        calculateExchangeRate();
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Error fetching static data:", err);
      setError(`${translation.failedToLoadData} ${err.response?.data?.detail || err.message}`);
      setLoading(false);
    }
  };

  // Calculate exchange rate based on selected currencies
  const calculateExchangeRate = () => {
    if (!window.exchangeRatesData || !window.exchangeRatesData.rates) {
      return;
    }
    
    const rates = window.exchangeRatesData.rates;
    const baseCurrency = window.exchangeRatesData.base;
    
    // Convert from base currency (usually USD) to target currencies
    if (currency1 === baseCurrency) {
      // Direct conversion
      setExchangeRate(rates[currency2]);
    } else if (currency2 === baseCurrency) {
      // Inverse conversion
      setExchangeRate(1 / rates[currency1]);
    } else {
      // Cross conversion
      setExchangeRate(rates[currency2] / rates[currency1]);
    }
  };

  const handleAmount1Change = (event) => {
    const value = event.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount1(value);
    }
  };

  const handleAmount2Change = (event) => {
    const value = event.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount2(value);
      if (exchangeRate && value) {
        // For the reverse calculation, we also want to avoid formatting
        const calculatedValue = parseFloat(value) / exchangeRate;
        setAmount1(calculatedValue.toString());
      } else {
        setAmount1('');
      }
    }
  };

  // Format numbers only when the user is done typing
  const handleAmount1Blur = () => {
    if (amount1) {
      const formattedValue = parseFloat(amount1).toFixed(2);
      setAmount1(formattedValue);
    }
  };

  const handleAmount2Blur = () => {
    if (amount2) {
      const formattedValue = parseFloat(amount2).toFixed(2);
      setAmount2(formattedValue);
    }
  };

  // Update handlers to set urlUpdated to true when currencies change manually
  const handleCurrency1Change = (e) => {
    setCurrency1(e.target.value);
    setUrlUpdated(true);
  };

  const handleCurrency2Change = (e) => {
    setCurrency2(e.target.value);
    setUrlUpdated(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {translation.loadingRates || 'Loading exchange rates...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: 500,
        height: 'auto',
        minHeight: 'auto',
        borderRadius: 8,
        border: '1px solid rgba(0, 0, 0, 0.28)',
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: 'white',
        boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.5)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {lastUpdated && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
          {translation.lastUpdated}: {lastUpdated} {translation.utc}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 0 }
        }}>
          <TextField
            placeholder={translation.enterAmount}
            value={amount1}
            onChange={handleAmount1Change}
            onBlur={handleAmount1Blur}
            name="amount1"
            sx={{ flex: 1 }}
            variant="outlined"
          />
          <Divider orientation="vertical" flexItem sx={{ 
            mx: 1, 
            height: '40px', 
            alignSelf: 'center', 
            display: { xs: 'none', sm: 'block' } 
          }} />
          <TextField
            select
            value={currency1}
            onChange={handleCurrency1Change}
            sx={{ width: { xs: '100%', sm: '150px' } }}
            variant="outlined"
            SelectProps={{
              renderValue: (value) => value
            }}
          >
            {Object.entries(currencies).map(([code, name]) => (
              <MenuItem key={code} value={code}>
                {code} - {name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 0 }
        }}>
          <TextField
            placeholder={translation.enterAmount}
            value={amount2}
            onChange={handleAmount2Change}
            onBlur={handleAmount2Blur}
            name="amount2"
            sx={{ flex: 1 }}
            variant="outlined"
          />
          <Divider orientation="vertical" flexItem sx={{ 
            mx: 1, 
            height: '40px', 
            alignSelf: 'center',
            display: { xs: 'none', sm: 'block' } 
          }} />
          <TextField
            select
            value={currency2}
            onChange={handleCurrency2Change}
            sx={{ width: { xs: '100%', sm: '150px' } }}
            variant="outlined"
            SelectProps={{
              renderValue: (value) => value
            }}
          >
            {Object.entries(currencies).map(([code, name]) => (
              <MenuItem key={code} value={code}>
                {code} - {name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </Card>
  );
}

export default ExchangeCalculator; 
